import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, TrendingUp, Eye } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export function AIAnalystHistory() {
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [showReportModal, setShowReportModal] = useState(false);

  // Fetch reports
  const { data: reports, isLoading: reportsLoading } = useQuery({
    queryKey: ["ai-analyst-reports"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("alpha_onchain_reports")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
  });

  // Fetch trading signals
  const { data: signals, isLoading: signalsLoading } = useQuery({
    queryKey: ["trading-signals-history"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trading_signals")
        .select("*")
        .not("ai_report", "is", null)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
  });

  const getReportTypeBadge = (type: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      daily: "default",
      weekly: "secondary",
      preview: "outline",
    };
    return <Badge variant={variants[type] || "default"}>{type}</Badge>;
  };

  const viewReport = (report: any) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="reports" className="w-full">
        <TabsList>
          <TabsTrigger value="reports">
            <FileText className="h-4 w-4 mr-2" />
            Relatórios
          </TabsTrigger>
          <TabsTrigger value="signals">
            <TrendingUp className="h-4 w-4 mr-2" />
            Sinais de Trading
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Relatórios</CardTitle>
              <CardDescription>Relatórios diários, semanais e preview gerados pela IA</CardDescription>
            </CardHeader>
            <CardContent>
              {reportsLoading ? (
                <div className="text-center py-8">Carregando...</div>
              ) : reports && reports.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Preview</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>
                          {format(new Date(report.created_at!), "dd/MM/yyyy HH:mm", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell>{getReportTypeBadge(report.report_type)}</TableCell>
                        <TableCell className="max-w-md truncate">
                          {report.content.substring(0, 100)}...
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewReport(report)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum relatório encontrado
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="signals">
          <Card>
            <CardHeader>
              <CardTitle>Sinais de Trading com Análise IA</CardTitle>
              <CardDescription>Sinais gerados com análise detalhada da IA</CardDescription>
            </CardHeader>
            <CardContent>
              {signalsLoading ? (
                <div className="text-center py-8">Carregando...</div>
              ) : signals && signals.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Ativo</TableHead>
                      <TableHead>Direção</TableHead>
                      <TableHead>Confiança</TableHead>
                      <TableHead>Entry/TP/SL</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signals.map((signal) => (
                      <TableRow key={signal.id}>
                        <TableCell>
                          {format(new Date(signal.created_at!), "dd/MM/yyyy HH:mm", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell className="font-medium">{signal.asset}</TableCell>
                        <TableCell>
                          <Badge variant={signal.direction === "long" ? "default" : "destructive"}>
                            {signal.direction.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>{signal.confidence_score}/10</TableCell>
                        <TableCell className="text-xs">
                          <div>E: ${signal.entry_price.toLocaleString()}</div>
                          <div>TP: ${signal.take_profit.toLocaleString()}</div>
                          <div>SL: ${signal.stop_loss.toLocaleString()}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              viewReport({ ...signal, content: signal.ai_report, report_type: "signal" })
                            }
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum sinal encontrado
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Report Detail Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedReport?.report_type === "signal" ? "Análise de Sinal" : "Relatório Completo"}
              {selectedReport?.report_type && getReportTypeBadge(selectedReport.report_type)}
            </DialogTitle>
            <DialogDescription>
              {selectedReport?.created_at &&
                format(new Date(selectedReport.created_at), "dd/MM/yyyy 'às' HH:mm", {
                  locale: ptBR,
                })}
            </DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{selectedReport?.content || ""}</ReactMarkdown>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
